import React, { CSSProperties } from "react";
import {
	Box,
	CopyButton,
	Group,
	Menu,
	ThemeIcon,
	UnstyledButton,
	Text,
	Button,
	useMantineTheme,
	Portal,
	Overlay
} from "@mantine/core";
import { IframeTypeToSupportedPlatformMap } from "../../../../Hooks";
import { useDisclosure } from "@mantine/hooks";
import { useInstallPrompt } from "../../../../Hooks/useInstallPrompt";
import { MdCheck, MdLanguage, MdOutlineLogin } from "react-icons/md";
import { Copy, Install, LogoOk, LogoTelegram, LogoVk, MenuBurger, Share } from "../../../../../../Icons";
import { useSharingHandlers } from "../../../../../../Stores";
import { useTranslation } from "react-i18next";
import {
	USER_STATUSES_WITH_LOGIN_ENABLED,
	USER_STATUSES_WITH_LOGOUT_ENABLED,
	capitalize
} from "../../../../../../Utils/Functions";
import { IconBase } from "react-icons";
import { useWebPlayLoginState } from "../../../../../../Stores/AppStore";
import { useUserStatus } from "../../../../../../Utils/Hooks/useUserStatus";
import { openLoginWithFirebaseModal } from "../../../../../../Components/Modals";
import * as Analytics from "../../../../../../Utils/Analytics";
import { FxIdDomainStoreEnumsSupportedWebPublishingPlatform } from "../../../../../../Api/gen";
import { FxIdDomainSettingsUrlOpenMethod } from "../../../../../../Api/gen/models/FxIdDomainSettingsUrlOpenMethod";
import { UserIdBlock } from "./UserIdBlock";
import { getGameUrl, handleContactsClick, handleTosPpDocsLinkClick, openLogoutModal } from "./helpers";
import { IFxIdSdkAdapterChangeLanguageRequest } from "../../../../../../SDK/FxIdSdkBaseAdapter";
import { getDocsAndLegal, getValueFromConfigWithOverrides } from "../../../../../../Config";
import { defaultLocale } from "@app/i18n";
import { webPlayerStore } from "@app/Screens/WebPlayer/store/webPlayerStore";

//#region Desktop Menu
const NestedMenu = React.forwardRef<HTMLButtonElement, React.PropsWithChildren<{ target: JSX.Element }>>(
	function NestedMenu(props, ref) {
		const { target, children } = props;
		return (
			<Menu position="left-start" width={210} trigger="hover" closeDelay={300}>
				<Menu.Target>
					<UnstyledButton
						sx={(theme) => ({
							display: "flex",
							width: "100%",
							"&:hover": {
								backgroundColor:
									theme.colorScheme === "dark"
										? theme.fn.rgba(theme.colors.dark[3], 0.35)
										: theme.colors.gray[1]
							}
						})}
						py="xs"
						px="sm"
					>
						{target}
					</UnstyledButton>
				</Menu.Target>
				{children}
			</Menu>
		);
	}
);

const ShareBadge = (props: { bgColor?: CSSProperties["backgroundColor"]; children: React.ReactNode }) => {
	const { children, bgColor = "#000000" } = props;
	return (
		<Box
			sx={{
				width: 20,
				height: 20,
				backgroundColor: bgColor,
				display: "inline-block",
				borderRadius: "50%",
				fontSize: 0
			}}
		>
			{children}
		</Box>
	);
};

type ChangeLanguageMenuProps = {
	languages: string[];
	onLanguageSelect?: () => void;
};

const ChangeLanguageMenuDesktop = (props: ChangeLanguageMenuProps) => {
	const { i18n, t } = useTranslation();
	const { languages, onLanguageSelect } = props;

	const theme = useMantineTheme();

	return (
		<NestedMenu
			target={
				<Group>
					<MdLanguage size={24} />
					{t("change_language")}
				</Group>
			}
		>
			<Menu.Dropdown>
				{languages.map((l) => (
					<Menu.Item
						key={l}
						p={5}
						onClick={() => {
							onLanguageSelect?.();
							void window.FxIdSdk?.ChangeLanguage(
								JSON.stringify({ lang: l } as IFxIdSdkAdapterChangeLanguageRequest)
							);
							void i18n.changeLanguage(l);
						}}
					>
						<Group spacing="xs" align="center">
							{i18n.language.toLowerCase() === l.toLowerCase() ? (
								<MdCheck color={theme.colors.blue[5]} size={20} />
							) : (
								<IconBase size={20} />
							)}
							{capitalize(new Intl.DisplayNames(l, { type: "language" }).of(l))}
						</Group>
					</Menu.Item>
				))}
			</Menu.Dropdown>
		</NestedMenu>
	);
};

const ShareMenu = React.forwardRef<HTMLButtonElement>(function ShareMenu() {
	const { t } = useTranslation();

	const adapter = webPlayerStore((state) => state.adapter);
	const gameConfig = webPlayerStore((state) => state.gameConfig);

	const { sharingHandlers, linkCopyEnabled } = useSharingHandlers();
	const shareVk = sharingHandlers.get("vk");
	const shareOk = sharingHandlers.get("ok");
	const shareTelegram = sharingHandlers.get("telegram");
	const handleShare = (handler: () => void, method: string) => {
		void Analytics.site.share({
			game: gameConfig.GameSystemName,
			sdk_adapter: adapter
				? FxIdDomainStoreEnumsSupportedWebPublishingPlatform[IframeTypeToSupportedPlatformMap[adapter]]
				: undefined,
			method
		});
		return handler();
	};
	const gameUrl = getGameUrl();
	const isAnySharingAllowed = [...sharingHandlers.values()].some((h) => h !== undefined) || linkCopyEnabled;

	if (!isAnySharingAllowed) return null;
	return (
		<NestedMenu
			target={
				<Group align="center" spacing="xs">
					<Share width={24} height={24} />
					<div>{t("web_player.menu.share")}</div>
				</Group>
			}
		>
			<Menu.Dropdown>
				{shareVk !== undefined && (
					<Menu.Item p={5} onClick={() => handleShare(shareVk, "vk")}>
						<Group spacing="xs">
							<ShareBadge bgColor="#4D76A1">
								<LogoVk width={20} height={20} />
							</ShareBadge>
							<Text sx={(theme) => ({ color: theme.colorScheme === "dark" ? theme.white : theme.black })}>
								ВКонтакте
							</Text>
						</Group>
					</Menu.Item>
				)}
				{shareOk !== undefined && (
					<Menu.Item p={5} onClick={() => handleShare(shareOk, "ok")}>
						<Group spacing="xs">
							<ShareBadge bgColor="#E27E35">
								<LogoOk width={20} height={20} />
							</ShareBadge>
							<Text sx={(theme) => ({ color: theme.colorScheme === "dark" ? theme.white : theme.black })}>
								Одноклассники
							</Text>
						</Group>
					</Menu.Item>
				)}
				{shareTelegram !== undefined && (
					<Menu.Item p={5} onClick={() => handleShare(shareTelegram, "telegram")}>
						<Group spacing="xs">
							<ShareBadge bgColor="#41BCE7">
								<LogoTelegram width={20} height={20} />
							</ShareBadge>
							<Text sx={(theme) => ({ color: theme.colorScheme === "dark" ? theme.white : theme.black })}>
								Telegram
							</Text>
						</Group>
					</Menu.Item>
				)}
				{linkCopyEnabled && (
					<CopyButton value={gameUrl.toString()}>
						{({ copied, copy }) => (
							<Menu.Item
								p={5}
								color={copied ? "green" : undefined}
								closeMenuOnClick={false}
								onClick={() => handleShare(copy, "link_copy")}
							>
								<Group spacing="xs">
									<ThemeIcon color={copied ? "green" : "#071E32"} size="sm" radius="xl">
										{copied ? (
											<MdCheck size={20} />
										) : (
											<Copy fill="currentColor" width={20} height={20} />
										)}
									</ThemeIcon>
									<Text>
										{copied ? t("web_player.menu.link_copied") : t("web_player.menu.copy_link")}
									</Text>
								</Group>
							</Menu.Item>
						)}
					</CopyButton>
				)}
			</Menu.Dropdown>
		</NestedMenu>
	);
});

export const MenuDesktop = () => {
	const { t, i18n } = useTranslation();
	const userStatus = useUserStatus();
	const isLoginEnabled = useWebPlayLoginState();
	const [opened, { close, open }] = useDisclosure(false);
	const gameConfig = webPlayerStore((state) => state.gameConfig);
	const portalConfig = webPlayerStore((state) => state.portalConfig);
	const adapter = webPlayerStore((state) => state.adapter);

	const { canInstall, showPrompt } = useInstallPrompt(gameConfig.GameSystemName);
	const docsFromPublicConfigAndBranding = getDocsAndLegal(portalConfig, gameConfig, adapter);
	const contactsUrl = getValueFromConfigWithOverrides<string>(
		portalConfig,
		"ContactsUrl",
		"ContactsUrl",
		adapter,
		gameConfig
	);

	log.info("docsFromPublicConfigAndBranding: ", docsFromPublicConfigAndBranding);

	// useWindowEvent("blur", () => close());

	const showLoginButton = isLoginEnabled && USER_STATUSES_WITH_LOGIN_ENABLED.includes(userStatus);
	const onFxId = adapter === "fxid" || adapter === undefined;
	const showLogoutButton = onFxId && USER_STATUSES_WITH_LOGOUT_ENABLED.includes(userStatus);

	// Если инфо для игры нет или специально указано что там undefined - то не показываем строчку
	let isContactsEnabled = false;
	if (onFxId) {
		const legalContacts = docsFromPublicConfigAndBranding.legalContacts
			? docsFromPublicConfigAndBranding.legalContacts
			: gameConfig.PublicWebClientConfig.LegalContacts;
		if (legalContacts != null) {
			const locale = i18n.resolvedLanguage ?? defaultLocale;

			// Если язык но он  undefined или нулл то ничего не показываем. Поидее это должно выше проверятся и сюда прийти не должны
			if (Object.prototype.hasOwnProperty.call(legalContacts, locale)) {
				const localeLegalInfo = legalContacts[locale];

				if (localeLegalInfo != null) {
					isContactsEnabled = true;
				}
			} else if (legalContacts.default != null) {
				isContactsEnabled = true;
			}
		}
	} else {
		if (docsFromPublicConfigAndBranding?.legalContacts) {
			isContactsEnabled = true;
		}
	}

	return (
		<Menu opened={opened} onChange={(newState) => (newState ? open() : close())} width={250}>
			<Portal target="#web-player">{opened && <Overlay opacity={0} />}</Portal>
			<Menu.Target>
				<UnstyledButton
					sx={(theme) => ({ display: "inline-flex", alignItems: "center", gap: theme.spacing.xs })}
					onClick={() => open()}
				>
					<Text lh="0px" mt={2} size="sm" c="white">
						{t("web_player.menu.button_title")}
					</Text>
					<MenuBurger fill="#fff" width={16} height={16} />
				</UnstyledButton>
			</Menu.Target>
			<Menu.Dropdown>
				{showLoginButton && (
					<Button
						compact
						fullWidth
						sx={{ height: 34 }}
						px={10}
						size="xs"
						rightIcon={<MdOutlineLogin size={22} />}
						onClick={() => {
							close();
							openLoginWithFirebaseModal({
								target: "#web-player",
								authProviders: {
									allowed:
										gameConfig?.PublicWebClientConfig.Auth?.AllowedAuthProviders ??
										portalConfig.PortalPublicWebConfig.CompanyBranding.Auth.AllowedAuthProviders
								},
								gameConfig: gameConfig,
								portalConfig: portalConfig,
								adapter: adapter,
								onAuthSuccessful: () => {
									log.info("Auth successful. Reloading");
									// Игра подписывается на addEventListener("beforeunload").
									// по хорошему тут надо был убрать это из игры и пинговать ее когда надо
									// удалить все эвент листенеры нельзя, поэтому попробуем просто поставить свойство обработчика собыитя
									// авось его установка сотрет листенеры
									window.onbeforeunload = () => {
										return undefined;
									};
									// window.removeEventListener("beforeunload");
									window.location.reload();
								}
							});
						}}
					>
						{t("sign_in_modal.title")}
					</Button>
				)}
				<Menu.Divider />
				{canInstall && (
					<>
						<Menu.Item icon={<Install width={24} height={24} />} onClick={() => void showPrompt()}>
							{t("web_player.menu.install")}
						</Menu.Item>
						<Menu.Divider />
					</>
				)}
				<Menu.Item component={ShareMenu} />
				{/* TODO: Вернуть, когда игра начнет поддерживать репорт проблемы */}
				{/* <Menu.Divider />
				<Menu.Item
					// TODO: Убрать fill после полного отключения бутстрапа
					icon={<Email fill="#000" width={24} height={24} />}
					onClick={() => void window.FxIdSdk?.CreateSupportRequest()}
				>
					{t("web_player.menu.report_issue")}
				</Menu.Item> */}
				<Menu.Divider />
				{gameConfig && (
					<ChangeLanguageMenuDesktop languages={gameConfig.SupportedLanguages} onLanguageSelect={close} />
				)}
				{showLogoutButton && (
					<>
						<Menu.Divider />
						<Menu.Item py={5} onClick={() => openLogoutModal(t, <Text>{t("logout_modal.text")}</Text>)}>
							<Text color="dimmed">{t("user_settings_menu.logout")}</Text>
						</Menu.Item>
					</>
				)}
				<Menu.Divider />
				<>
					{docsFromPublicConfigAndBranding.pp && (
						<Menu.Item
							onClick={() =>
								handleTosPpDocsLinkClick(
									String(docsFromPublicConfigAndBranding.pp?.url) || "",
									docsFromPublicConfigAndBranding.pp?.openMethod ||
										FxIdDomainSettingsUrlOpenMethod.Unspecified
								)
							}
						>
							{t("pp")}
						</Menu.Item>
					)}
					{docsFromPublicConfigAndBranding.tos && (
						<Menu.Item
							onClick={() =>
								handleTosPpDocsLinkClick(
									String(docsFromPublicConfigAndBranding.tos?.url) || "",
									docsFromPublicConfigAndBranding.tos?.openMethod ||
										FxIdDomainSettingsUrlOpenMethod.Unspecified
								)
							}
						>
							{t("tos")}
						</Menu.Item>
					)}
					<Menu.Divider />
				</>

				{isContactsEnabled && (
					<>
						<Menu.Item onClick={() => handleContactsClick(gameConfig.GameSystemName, adapter, contactsUrl)}>
							{t("legal_contacts")}
						</Menu.Item>
						<Menu.Divider />
					</>
				)}
				<UserIdBlock />
			</Menu.Dropdown>
		</Menu>
	);
};
